import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PassportAiNoteProps {
  className?: string;
}

/**
 * Small info affordance for the AI-derived passport pages (Stations / Skills /
 * Map). The data is extracted from the CV by an LLM, so it can be incomplete or
 * inaccurate; this surfaces that transparently without cluttering the view.
 * Renders an absolutely-positioned icon, so the parent container must be
 * `relative`.
 */
const PassportAiNote: React.FC<PassportAiNoteProps> = ({ className = '' }) => {
  const { t } = useTranslation('passport');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div ref={ref} className={`absolute top-2 right-2 z-20 ${className}`}>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t('aiNote.title')}
        className="p-1.5 rounded-full bg-white/80 backdrop-blur text-slate-400 hover:text-slate-600 hover:bg-white border border-slate-200/70 shadow-sm transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-[min(18rem,calc(100vw-2rem))] rounded-xl bg-white border border-slate-200 shadow-lg p-3 z-30">
          <p className="text-[12px] font-semibold text-slate-800">{t('aiNote.title')}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{t('aiNote.body')}</p>
        </div>
      )}
    </div>
  );
};

export default PassportAiNote;
